/** DOM manipulation and style utilities */

import type { CSSProperties } from 'react'
import type { Session, InputField } from '../types'
import { StepButtonType } from '../constants'

/** React's `style` prop rejects raw CSS strings; backend sometimes sends them. */
export function parseStyleProp(style: unknown): CSSProperties | undefined {
  if (style == null) {
    return undefined
  }
  if (typeof style === 'object') {
    return style as CSSProperties
  }
  if (typeof style !== 'string') {
    return undefined
  }
  const result: Record<string, string> = {}
  for (const decl of style.split(';')) {
    const idx = decl.indexOf(':')
    if (idx === -1) {
      continue
    }
    const rawProp = decl.slice(0, idx).trim()
    const value = decl.slice(idx + 1).trim()
    if (!rawProp || !value) {
      continue
    }
    const prop = rawProp.startsWith('--') ? rawProp : rawProp.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
    result[prop] = value
  }
  return result as CSSProperties
}

/**
 * Get CSS classes from object with classes property
 */
export function getClassFromObject(obj: { classes?: string[] } | null | undefined): string {
  return obj?.classes ? obj.classes.join(' ') : ''
}

/**
 * Check if input button is outline style
 */
export function isOutlineButton(input: InputField): boolean {
  return (
    input.button_styles &&
    [StepButtonType.FlatOutline, StepButtonType.ModernOutline].includes(input.button_styles.style_type)
  )
}

/**
 * Get custom input CSS classes
 */
export function getCustomInputClass(session: Session, input: InputField, includeButtonClass: boolean = false): string {
  let classes = getClassFromObject(input as { classes?: string[] })

  if (includeButtonClass) {
    if (!session.is_legacy) {
      classes += ' input-btn mb-0'
      if (isOutlineButton(input)) {
        classes += ' input-outline-btn'
      }
    } else {
      classes += ' text-capitalize'
    }
  }

  return classes
}

/**
 * Set placeholder labels from DOM
 */
export function setPlaceholders(placeholders: Record<string, string>): void {
  const placeholderLabels = Array.from(document.querySelectorAll('.placeholder-label')) as HTMLElement[]

  placeholderLabels.forEach(elem => {
    const target = elem.dataset.for
    if (target) {
      placeholders[target] = elem.innerText
    }
  })
}

/**
 * Scroll to top of page
 */
export function scrollToTop(): void {
  if (typeof window !== 'undefined') {
    try {
      window.scroll({ top: 0, left: 0, behavior: 'smooth' })
    } catch {
      window.scrollTo(0, 0)
    }
  }
}

/**
 * Add new styles to document
 */
export function addNewStyles(id: string, styles: string): void {
  if (typeof document === 'undefined') {
    return
  }

  let styleElement = document.getElementById(id) as HTMLStyleElement

  if (!styleElement) {
    styleElement = document.createElement('style')
    styleElement.id = id
    document.head.appendChild(styleElement)
  }

  styleElement.textContent = styles
}

/**
 * Apply CSS variables for theming
 */
export function applyCSSVariables(variables: Record<string, string>): void {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement

  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key.startsWith('--') ? key : `--${key}`, value)
  })
}

/**
 * Remove CSS variables
 */
export function removeCSSVariables(variables: string[]): void {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement

  variables.forEach(key => {
    root.style.removeProperty(key.startsWith('--') ? key : `--${key}`)
  })
}

/**
 * Check screen size and apply responsive font sizing
 */
export function checkFontScreenSize(mediaWidth: number): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return window.innerWidth <= mediaWidth
}

/**
 * Get element by data attribute
 */
export function getElementByData(attribute: string, value: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null
  }
  return document.querySelector(`[data-${attribute}="${value}"]`)
}

/**
 * Get elements by data attribute
 */
export function getElementsByData(attribute: string, value?: string): HTMLElement[] {
  if (typeof document === 'undefined') {
    return []
  }

  const selector = value ? `[data-${attribute}="${value}"]` : `[data-${attribute}]`
  return Array.from(document.querySelectorAll(selector))
}

/**
 * Add event listener with cleanup
 */
export function addEventListenerWithCleanup(
  element: HTMLElement | Window | Document,
  event: string,
  handler: EventListener,
  options?: AddEventListenerOptions
): () => void {
  element.addEventListener(event, handler, options)

  return () => {
    element.removeEventListener(event, handler, options)
  }
}
